// A read-only photo grid + fullscreen lightbox, shared by any page that shows a
// set of image URLs (the athlete/coach profile page's photos, both the
// authenticated and the public view). Extracted from the company-photos grid so
// the CSS grid and the `PhotoLightbox` wiring live in one place. Purely
// presentational: it takes plain URLs and pre-translated labels, so it works
// inside and outside the authenticated app (the public tree has no AuthContext).

import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CollectionsIcon from '@mui/icons-material/Collections';
import { PhotoLightbox, type LightboxPhoto } from './PhotoLightbox';

export interface PhotoGalleryProps {
  /** Public image URLs to show, in order. */
  urls: string[];
  /** Alt text for each image (also the lightbox alt). */
  alt: string;
  /** Message shown when there are no photos. */
  emptyLabel: string;
  /** Pre-translated lightbox chrome labels. */
  labels: {
    close: string;
    download: string;
    previous: string;
    next: string;
  };
}

export function PhotoGallery({ urls, alt, emptyLabel, labels }: PhotoGalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (urls.length === 0) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
        <CollectionsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Stack>
    );
  }

  const photos: LightboxPhoto[] = urls.map((url, i) => ({
    id: `${i}`,
    url,
    comment: '',
  }));

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 2,
        }}
      >
        {photos.map((photo, i) => (
          <Paper key={photo.id} variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box
              component="img"
              src={photo.url}
              alt={alt}
              loading="lazy"
              onClick={() => setLightboxIndex(i)}
              sx={{
                width: '100%',
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                display: 'block',
                cursor: 'zoom-in',
              }}
            />
          </Paper>
        ))}
      </Box>

      <PhotoLightbox
        photos={photos}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        showDownload
        labels={{ ...labels, alt }}
      />
    </>
  );
}
